import React from 'react';
import PropTypes from 'prop-types';

import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PostAddIcon from '@mui/icons-material/PostAdd';
import * as Sentry from '@sentry/browser';
import { v4 as uuidv4 } from 'uuid';

import useStorage from '../../../hooks/useStorage';
import useToast from '../../hooks/useToast';
import { downloadBlobAsFile, downloadUrlAsFile } from '../../../utils/file';
import FileSelectButton from '../../../components/FileSelectButton';
import LoadingOverlay from '../LoadingOverlay';
import {
  FileUploadContainer,
  FileList,
  FileItem,
  FileName,
  StyledButton,
  StyledIconButton,
} from './styles';
import texts from './texts.json';

const BLOB_URL_PREFIX = 'blob:';

const AttachmentsContainer = ({
  fileAttachments,
  onFilesSelected,
  onFileRemoved,
  isUploading,
  className,
  disabled,
}) => {
  const { getBlob, getBlobUrl } = useStorage();
  const { showToast } = useToast();

  // A file that has not been uploaded yet is already a local blob URL owned by the caller; anything
  // else is a storage ref that has to be fetched.
  const isLocalUrl = (url) => url.startsWith(BLOB_URL_PREFIX);

  // Fetching an attachment fails on a stale or deleted ref, or with no connection. Without this the
  // coach gets an unhandled rejection and a button that silently does nothing.
  const reportFileError = (error, url) => {
    Sentry.captureException(error, {
      extra: {
        attachmentUrl: url,
      },
    });
    showToast(texts.fileError, { error: true });
  };

  const openFileInNewTab = async (url) => {
    if (!url) return;
    try {
      const fileUrl = isLocalUrl(url) ? url : await getBlobUrl(url);
      window.open(fileUrl, '_blank');
    } catch (error) {
      reportFileError(error, url);
    }
  };

  const downloadFile = async (url, name) => {
    if (!url) return;
    try {
      if (isLocalUrl(url)) {
        await downloadUrlAsFile(url, name);
      } else {
        // Resolving to the blob rather than a blob URL leaves file-saver to create and revoke the
        // object URL, and lets the saved name be corrected from the blob's real type.
        downloadBlobAsFile(await getBlob(url), name);
      }
    } catch (error) {
      reportFileError(error, url);
    }
  };

  return (
    <FileUploadContainer className={className}>
      <FileSelectButton
        ButtonComponent={StyledButton}
        onSelect={onFilesSelected}
        multiple
        disabled={disabled}
      >
        <PostAddIcon />
        {texts.attachFile}
      </FileSelectButton>
      <FileList>
        {fileAttachments.map((file, index) => {
          const name = file.name || file.file.name;
          const url = file.dataUrl || file.url;
          return (
            <FileItem key={`${name}-${uuidv4()}`}>
              <FileName
                onClick={() => openFileInNewTab(url)}
              >
                {name}
              </FileName>
              {!!url && (
                <StyledIconButton
                  title={texts.download}
                  onClick={() => downloadFile(url, name)}
                >
                  <FileDownloadIcon fontSize="inherit" />
                </StyledIconButton>
              )}
              <StyledIconButton onClick={() => onFileRemoved(index)}>
                <ClearIcon fontSize="inherit" />
              </StyledIconButton>
            </FileItem>
          );
        })}
      </FileList>
      <LoadingOverlay isLoading={isUploading && fileAttachments.length !== 0} />
    </FileUploadContainer>
  );
};

AttachmentsContainer.propTypes = {
  fileAttachments: PropTypes.array.isRequired,
  onFilesSelected: PropTypes.func.isRequired,
  onFileRemoved: PropTypes.func.isRequired,
  isUploading: PropTypes.bool.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

AttachmentsContainer.defaultProps = {
  className: '',
  disabled: false,
};

export default AttachmentsContainer;
